'use client'

import { Slot } from '@radix-ui/react-slot'
import copy from 'copy-to-clipboard'
import React, { useState } from 'react'

import { Tooltip } from '@/components/base/tooltip'
import { useI18n } from '@/i18n/i18n-react'
import { CheckCircleMiniSolid } from '@/icons/check-circle-mini-solid'
import { CheckCircleSolid } from '@/icons/check-circle-solid'
import { SquareTwoStack } from '@/icons/square-two-stack'
import { SquareTwoStackMini } from '@/icons/square-two-stack-mini'
import { clx } from '@/utils/clx'

type CopyProps = React.HTMLAttributes<HTMLButtonElement> & {
	content: string
	variant?: 'mini' | 'default' | null
	asChild?: boolean
	tooltipText?: string
}

/**
 * This component is based on the `button` element and supports all of its props
 */
const Copy = React.forwardRef<HTMLButtonElement, CopyProps>(
	(
		{
			children,
			className,
			/**
			 * The content to copy.
			 */
			content,
			/**
			 * The variant of the copy button.
			 */
			variant = 'default',
			/**
			 * Whether to remove the wrapper `button` element and use the
			 * passed child element instead.
			 */
			asChild = false,
			tooltipText,
			...props
		}: CopyProps,
		ref
	) => {
		const { L } = useI18n()
		const [done, setDone] = useState(false)
		const [open, setOpen] = useState(false)
		const [text, setText] = useState(tooltipText || L.General.Copy())

		const copyToClipboard = (
			e: React.MouseEvent<HTMLElement, MouseEvent> | React.MouseEvent<HTMLButtonElement, MouseEvent>
		) => {
			e.stopPropagation()

			setDone(true)
			copy(content)

			setTimeout(() => {
				setDone(false)
			}, 2000)
		}

		React.useEffect(() => {
			if (done) {
				setText(L.General.Copied())
				return
			}

			setTimeout(() => {
				setText(tooltipText || L.General.Copy())
			}, 500)
		}, [done])

		const isDefaultVariant = (variant?: string | null): variant is 'default' => {
			return variant === 'default'
		}

		const isDefault = isDefaultVariant(variant)

		const Component = asChild ? Slot : 'button'

		return (
			<Tooltip content={text} open={done || open} onOpenChange={setOpen}>
				<Component
					ref={ref}
					aria-label='Copy code snippet'
					type='button'
					className={clx('h-fit w-fit', className)}
					onClick={copyToClipboard}
					{...props}
				>
					{children ? (
						children
					) : done ? (
						isDefault ? (
							<CheckCircleSolid className='text-ui-fg-subtle' />
						) : (
							<CheckCircleMiniSolid className='text-ui-fg-subtle' />
						)
					) : isDefault ? (
						<SquareTwoStack className='text-ui-fg-subtle' />
					) : (
						<SquareTwoStackMini className='text-ui-fg-subtle' />
					)}
				</Component>
			</Tooltip>
		)
	}
)
Copy.displayName = 'Copy'

export { Copy }
